/**
 * projectStore
 *
 * Single source of truth for which project is currently active.
 * Components that need project data subscribe to this store; stores
 * that need to reload on a switch (engineStore, propertiesStore,
 * viewerStore) react to changes via App-level effects.
 *
 * Switching projects always invokes `setCurrentProject(id)` from
 * the header's ProjectSwitcher. App.tsx and FormaApp.tsx watch the
 * id and re-fire the loaders, which reload element-facts.json /
 * properties.json from the new project's URLs and reset the viewer
 * to the new default sheet.
 */
import { create } from "zustand";
import { DEFAULT_PROJECT_ID, getProject, type Project } from "../data/projects";

interface ProjectStore {
  currentProjectId: string;
  setCurrentProject: (id: string) => void;
  getCurrentProject: () => Project;
}

export const useProjectStore = create<ProjectStore>((set, get) => ({
  currentProjectId: DEFAULT_PROJECT_ID,
  setCurrentProject: (id: string) =>
    set(state =>
      state.currentProjectId === id ? state : { currentProjectId: id }
    ),
  getCurrentProject: () => getProject(get().currentProjectId),
}));

/** Convenience hook for components that just need the active Project. */
export function useCurrentProject(): Project {
  return useProjectStore(s => getProject(s.currentProjectId));
}
